import { FC } from 'react';
import {
  AutocompleteInput,
  Modal,
  PhoneInput,
  TextField,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { Stack } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { usePredefinedToasts } from 'utils/Toast';
import { APP } from 'config';
import { useAdminCreateClient } from 'store/admin-clients';
import { formatCountry } from 'utils/format';

interface Values {
  companyCountry: string;
  companyName: string;
  companyCode: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminPhone: string;
  internalComment: string;
}

const DEFAULT_VALUES: Values = {
  companyCountry: APP.countries.default,
  companyName: '',
  companyCode: '',
  adminFirstName: '',
  adminLastName: '',
  adminEmail: '',
  adminPhone: '',
  internalComment: '',
};

interface Props extends ModalProps {}

export const CreateClientModal: FC<Props> = ({ closeModal }) => {
  const { t, i18n } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: DEFAULT_VALUES,
  });

  const { mutate: createClient, isLoading: isCreating } =
    useAdminCreateClient();

  const onSubmit: SubmitHandler<Values> = (values) => {
    createClient(
      { ...values },
      {
        onSuccess: () => {
          closeModal();
          predefinedToasts.success.added();
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('client.add_client')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.close'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
            disabled: !isDirty || isCreating,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="companyCountry"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <AutocompleteInput
                label={t('benefits.country')}
                name={name}
                value={value}
                onChange={onChange}
                options={APP.countries.alpha3.map((alpha3) => ({
                  value: alpha3,
                  label: formatCountry(i18n.language, alpha3),
                }))}
                required
                multiple={false}
                ref={ref}
              />
            )}
          />

          <Controller
            name="companyName"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('companies.company_name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="companyCode"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('companies.company_code')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="adminEmail"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('client.admin_email')}
                name={name}
                value={value}
                onChange={onChange}
                type="email"
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="adminFirstName"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('client.admin_first_name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="adminLastName"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('client.admin_last_name')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="adminPhone"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <PhoneInput
                label={t('client.admin_phone')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="internalComment"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <TextField
                  label={t('common.internal_comment')}
                  rows={5}
                  name={name}
                  value={value}
                  onChange={onChange}
                  ref={ref}
                />
              );
            }}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
